import { memo, useMemo, useState } from 'react';
import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';
import ChartPanel from './ChartPanel';
import SegmentedToggle from './SegmentedToggle';
import MetricMultiSelect from './MetricMultiSelect';
import InfoTip from '../../common/InfoTip';
import DisclaimerMarker from './DisclaimerMarker';
import useAnnualPercentChart from './useAnnualPercentChart';
import { buildAnnualScores } from '../utils/qualityScore';
import { buildEstimatedPlotBand } from '../hooks/seriesMath';
import {
  ANNUAL_LINE_COLORS, hasPercentPoint, percentLine, toPercentPoints,
} from '../config/percentChartConfig';
import styles from './RatioChartCard.module.css';

const SCORE_MODE_OPTIONS = [
  { value: 'score', label: 'Score' },
  { value: 'underlying', label: 'Underlying' },
];

const SCORE_OPTIONS = [
  { value: 'quality', label: 'Quality', color: ANNUAL_LINE_COLORS.quality, points: 'quality', unitSuffix: '' },
  { value: 'growthTotal', label: 'Growth (total)', color: ANNUAL_LINE_COLORS.growth, points: 'growthScoreTotal', unitSuffix: '' },
  { value: 'growthPerShare', label: 'Growth (per share)', color: ANNUAL_LINE_COLORS.growthPerShare, points: 'growthScorePerShare', unitSuffix: '' },
];

// Every row is a genuine input to a scored term AND a percentage, so the
// percent axis is honest for all of them. Asset turnover is deliberately absent
// (a multiple, not a percentage) and so is FCF margin (FCF quality scores FCF
// growth, level stability and FCF-vs-earnings — never FCF / revenue, so a line
// would imply a pillar input that does not exist).
const UNDERLYING_OPTIONS = [
  { value: 'netMargin', label: 'Net margin', color: ANNUAL_LINE_COLORS.netMargin, points: 'netMargin' },
  { value: 'ebitdaMargin', label: 'EBITDA margin', color: ANNUAL_LINE_COLORS.ebitdaMargin, points: 'ebitdaMargin' },
  { value: 'revenueGrowthTotal', label: 'Revenue growth (total)', color: ANNUAL_LINE_COLORS.revenueGrowth, points: 'revenueGrowthTotal' },
  { value: 'revenueGrowthPerShare', label: 'Revenue growth (per share)', color: ANNUAL_LINE_COLORS.revenueGrowthPerShare, points: 'revenueGrowthPerShare' },
  { value: 'shareGrowth', label: 'Share-count growth', color: ANNUAL_LINE_COLORS.shareGrowth, points: 'shareGrowth' },
  { value: 'netDebtToValue', label: 'Net debt / company value', color: ANNUAL_LINE_COLORS.netDebtToValue, points: 'netDebtToValue' },
  { value: 'fcfGrowth', label: 'FCF growth', color: ANNUAL_LINE_COLORS.fcfGrowth, points: 'fcfGrowth' },
  { value: 'roa', label: 'ROA', color: ANNUAL_LINE_COLORS.roa, points: 'roa', ratioKey: 'roa' },
  { value: 'roic', label: 'ROIC', color: ANNUAL_LINE_COLORS.roic, points: 'roic', ratioKey: 'roic' },
];

const DEFAULT_SELECTED = {
  score: ['quality', 'growthTotal', 'growthPerShare'],
  underlying: ['netMargin', 'ebitdaMargin', 'revenueGrowthTotal', 'revenueGrowthPerShare'],
};

// Quality is computed on the total-company basis, so the per-share/total toggle
// never moves it (dilution is already its own quality pillar — see
// buildAnnualScores). As of 2026-10-29 the Market Cap / Enterprise Value
// toggle no longer moves the quality score at all: the leverage pillar
// switched to FCF coverage (SCORE_CARDS.md §1.6), which takes no price input.
// The toggle still moves the "Net debt / company value" UNDERLYING-mode
// display line and its label (a value-basis-aware convention this repo keeps
// for that line specifically) — just not the score. Neither tip mentions
// forward estimates — that caveat lives only in the quality-score footnote,
// not the hover label.
const INFO_TEXT = {
  score: `Quality is a 0–100 house score meant to pick out high-quality compounders: businesses that earn more year after year on steady margins, generate cash, do not dilute and are not over-indebted. Consistency is weighted most (−40 to +40 points: earnings per share rising over the last five years, and stable operating margins), then free-cash-flow quality after stock compensation (up to 25), profitability and debt coverage (up to 30 each) and revenue growth (up to 15); dilution can only subtract. Banks and insurers have no consistency part and use a net-income measure (capped at 30 of 40) in place of free cash flow. The parts add up to more than 100, so near-perfection on most of them reaches 100. Growth is a Rule-of-40-style score on a 0–100 scale, total and per share: each year adds revenue growth (up to 100%) to three-quarters of that year’s EBITDA margin (operating cash flow for REITs, counted between −100% and 40%), and 70% or more scores 100. Total uses the lower of total and per-share growth, so growth paid for with new shares does not count. Banks and other lenders (financials whose revenue is mostly interest income) are scored on revenue net of interest expense, with no margin credit and no forward estimate. Only growth to a new high counts: growth that recovers toward the revenue peak of the previous five years earns nothing, and growth counts less when revenue fell in more than 20% of up to the last ten years (nothing above 60%). Below 15% revenue growth the score is capped (5% → at most 33, 10% → at most 67, below or falling growth → 0), a year with zero or negative revenue, or growing from it, scores 0, and the line is the 3-year average (at most 30 after a year of zero or negative revenue). Banks, insurers and other financials are scored on equity to assets and REITs on net debt to EBITDA instead of debt coverage; goodwill-heavy or negative-equity financials stay on debt coverage. Quality measures accounting stability and looks backward: for banks and insurers, the main risks (balance-sheet leverage, liquidity and tail events) are not fully visible in it.`,
  underlying: 'The inputs behind the scores. Net margin (capped at operating margin) and operating ROA feed the quality score’s profitability pillar; share-count growth feeds dilution; the FCF level and net debt feed leverage; FCF growth and revenue growth feed the FCF- and revenue-quality pillars; earnings per share and operating margin (not plotted) feed consistency; EBITDA margin and both revenue-growth lines feed the Growth score, which counts each year’s margin between −100% and 40% and uses operating cash flow margin instead for REITs; the EBITDA margin line shows the reported figure itself. Net debt / company value is shown for reference and follows the page’s Market Cap / Enterprise Value toggle, but no longer feeds the leverage pillar, which is free-cash-flow coverage instead. Both revenue-growth lines are always shown, so neither follows the page’s per-share/total toggle. Quality uses the screener’s arithmetic on the same raw statement data.',
};

const EMPTY_HISTORY = 'Not enough annual history for this score yet.';

const ScoresCard = ({
  highcharts,
  price = [],
  shares = [],
  netDebt = [],
  revenue = [],
  ebitda = [],
  fcf = [],
  incomeAnnual = [],
  // Raw statement rows the Quality score runs on (useStockData's qualityInputs).
  qualityInputs = null,
  ratios = null,
  valueBasis = 'marketCap',
  displayScale = 'perShare',
  embedded = false,
  // The main/ratio charts' fetch-time default window (useStockData's
  // `defaultRange`), so this chart opens on the same window as the ratio chart.
  defaultRange = null,
}) => {
  // One list per mode: the two modes have different series, so a shared
  // selection could not address both.
  const [scoreSelected, setScoreSelected] = useState(DEFAULT_SELECTED.score);
  const [underlyingSelected, setUnderlyingSelected] = useState(DEFAULT_SELECTED.underlying);
  const [mode, setMode] = useState('score');
  const rawSelected = mode === 'score' ? scoreSelected : underlyingSelected;
  const setSelected = mode === 'score' ? setScoreSelected : setUnderlyingSelected;

  const model = useMemo(() => buildAnnualScores({
    price,
    shares,
    netDebt,
    valueBasis,
    displayScale,
    revenue,
    ebitda,
    fcf,
    incomeAnnual,
    qualityInputs,
    roa: ratios?.roa ?? null,
    roic: ratios?.roic ?? null,
  }), [
    price, shares, netDebt, valueBasis, displayScale, revenue, ebitda, fcf, incomeAnnual, qualityInputs,
    ratios,
  ]);

  const options = useMemo(() => {
    if (mode === 'score') return SCORE_OPTIONS;
    return UNDERLYING_OPTIONS.map((item) => ({
      ...item,
      // Label and math both follow valueBasis — a "Net debt / market cap" label
      // over an enterprise-value ratio is the specific bug this avoids.
      label: item.value === 'netDebtToValue'
        ? `Net debt / ${valueBasis === 'enterpriseValue' ? 'enterprise value' : 'market cap'}`
        : item.label,
      disabled: item.ratioKey ? !Array.isArray(ratios?.[item.ratioKey]) : false,
    }));
  }, [mode, ratios, valueBasis]);

  const selected = useMemo(() => rawSelected.filter((value) => {
    const option = options.find((item) => item.value === value);
    return option && !option.disabled;
  }), [rawSelected, options]);

  // Reported years, then the forward estimate spine. No TTM point on this
  // card: its Quality reading was structurally capped by missing pillars
  // (SCORE_CARDS.md §5, 2026-10-31). A series with no point at a given slot
  // simply ends in a null — a gap under connectNulls: false, which renders nothing.
  const allYears = useMemo(
    () => [...model.years, ...model.forwardYears],
    [model],
  );

  const series = useMemo(() => options
    .filter((item) => selected.includes(item.value))
    .map((item) => percentLine(
      `scores-${item.value}`,
      item.label,
      toPercentPoints(allYears, model[item.points]),
      item.color,
      item.unitSuffix ?? '%',
    )), [options, selected, allYears, model]);

  const emptyMessage = selected.length > 0 && !series.some((line) => hasPercentPoint(line.data))
    ? EMPTY_HISTORY
    : null;
  // Same idiom as the ratio/yield card: the line continues into the forward
  // region unchanged and the region itself is a grey x-axis band labelled
  // "Estimated" with the implied-estimates footnote marker. The band stops
  // where a TICKED line actually reaches, in EITHER mode — with only
  // `Net margin` ticked (no forward point) nothing is projected, and a grey
  // "Estimated" band over empty space would be a claim about nothing.
  const estimatedBand = useMemo(() => {
    const forwardX = new Set(model.forwardYears.map(({ x }) => x));
    const reached = series
      .flatMap((line) => line.data)
      .filter((point) => point.y != null && forwardX.has(point.x))
      .map((point) => point.x);
    if (!reached.length) return [];
    return buildEstimatedPlotBand(price, [[Math.max(...reached), null]]);
  }, [price, model, series]);
  const { chartRef, chartOptions } = useAnnualPercentChart(
    series,
    !emptyMessage,
    mode === 'score' ? 'Score (0–100)' : 'Percent',
    estimatedBand,
    defaultRange,
  );

  const inner = (
    <>
      <div className={`${styles.header}${embedded ? ` ${styles.headerEmbedded}` : ''}`}>
        {!embedded && (
          <h2 className={styles.title}>
            Scores
            <span className={styles.betaTag}>Beta</span>
            <DisclaimerMarker id="quality-score" />
          </h2>
        )}
        <div className={styles.headerControls}>
          {embedded && <span className={styles.betaTag}>Beta</span>}
          <MetricMultiSelect
            selected={selected}
            onChange={setSelected}
            options={options}
          />
          <SegmentedToggle
            options={SCORE_MODE_OPTIONS}
            value={mode}
            onChange={setMode}
            ariaLabel="Score chart mode"
          />
          <InfoTip text={INFO_TEXT[mode]} />
          {embedded && (
            <span className={styles.footnoteLabel}>
              Notes
              <DisclaimerMarker id="quality-score" />
            </span>
          )}
        </div>
      </div>
      {emptyMessage ? (
        <p className={styles.empty}>{emptyMessage}</p>
      ) : (
        <ErrorBoundary message="Chart couldn't render.">
          <ChartPanel ref={chartRef} highcharts={highcharts} chartOptions={chartOptions} />
        </ErrorBoundary>
      )}
    </>
  );

  return embedded ? inner : <section className={styles.card}>{inner}</section>;
};

export default memo(ScoresCard);
